import { Box, Chip, IconButton, Paper, Tooltip } from '@mui/material';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { IoMdInfinite } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';

import {
    getAllAssetItems,
    resetAssetItem,
    selectAllAssetItem,
    selectAllAssetItemStatus,
} from 'features/items/itemsTableSlice';
import { fetchAllZones } from 'features/zones/zonesTableSlice';
import {
    addZonesToActiveAsset,
    getActiveAssetActiveZone,
    getChildZones,
    selectLoadedZones,
    setAllZonesOnActiveAsset,
} from 'GeminiViewerComponent/features/asset/assetSlice';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { LoadingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { getUnPositionedItems } from 'GeminiViewerComponent/helpers/hotspot_helpers';
import { uniqBy } from 'GeminiViewerComponent/helpers/lodashUtils';
import { panoDraggable } from '../../../PanoControls/PanoDraggable';
import { useStyles } from './styles';

const UnPositionedItemsBar = ({ itemChipsRef, onMovedHotspot, assetId, zoneCount }) => {
    //#region Constants
    const dispatch = useDispatch();
    //#endregion Constants

    //#region Hooks
    //#endregion Hooks

    //#region State
    const [isDisplayAllZone, setIsDisplayAllZone] = useState(false);
    const activeZone = useSelector(getActiveAssetActiveZone);
    const unPositionedItems = getUnPositionedItems(activeZone);
    const [allZoneItems, setAllZoneItems] = useState([...unPositionedItems]);
    //#endregion State

    //#region Selectors
    const theme = useSelector(selectDefaultTheme);
    const classes = useStyles(theme);
    const allAssetItem = useSelector(selectAllAssetItem);
    const allAssetItemStatus = useSelector(selectAllAssetItemStatus);
    const loadedZones = useSelector(selectLoadedZones);
    const childZones = useSelector(getChildZones);
    //#endregion Selectors

    //#region Refs
    //#endregion Refs

    //#region Effects
    useEffect(() => {
        if (allAssetItemStatus === LoadingStatus.Idle) {
            fetchItemAsset(allAssetItemStatus);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allAssetItemStatus]);

    useEffect(() => {
        updateList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeZone?.items]);

    useEffect(() => {
        return () => {
            dispatch(resetAssetItem());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    //#endregion Effects

    //#region Methods
    const fetchItemAsset = async (allAssetItemStatus) => {
        if (allAssetItemStatus === LoadingStatus.Loading) return;
        await dispatch(getAllAssetItems({ assetId: assetId }));
        if (uniqBy([...loadedZones, ...childZones], (z) => z.zone_id).length < zoneCount) {
            const zones = await dispatch(
                fetchAllZones({
                    includeItems: true,
                    assetId,
                })
            );
            await dispatch(
                addZonesToActiveAsset({
                    assetId,
                    zoneInfo: { zones: zones.payload.zones },
                })
            );
            await dispatch(
                setAllZonesOnActiveAsset({
                    assetId,
                    zoneInfo: { zones: zones.payload.zones },
                })
            );
        }
    };

    const toggleZoneMedia = useCallback(
        async (displayAllZone, isToggle = true) => {
            if (displayAllZone) {
                if (allAssetItem && allAssetItem?.length > 0) {
                    const assetUnpositionedItems = allAssetItem?.filter(
                        (item) => (!item.yaw && !item.flat_x) || (!item.flat_x && (item.flat_x < 0 || item.flat_y < 0))
                    );
                    setAllZoneItems([...assetUnpositionedItems]);
                }
            } else {
                if (activeZone?.items && activeZone?.items?.length > 0) {
                    const currentZoneUnpositionedItems = activeZone?.items?.filter(
                        (item) => (!item.yaw && !item.flat_x) || (!item.flat_x && (item.flat_x < 0 || item.flat_y < 0))
                    );
                    setAllZoneItems([...currentZoneUnpositionedItems]);
                }
            }
            if (isToggle) {
                setIsDisplayAllZone((prev) => !prev);
            }
        },
        [activeZone?.items, allAssetItem]
    );

    const updateList = useCallback(() => {
        toggleZoneMedia(isDisplayAllZone, false);
    }, [isDisplayAllZone, toggleZoneMedia]);

    const isCurrentZoneItem = activeZone?.items?.filter((item) => !item?.flat_x && !item?.flat_y);
    const isAllZoneItem =
        allAssetItem && allAssetItem?.length > 0 && allAssetItem?.filter((item) => !item?.flat_x && !item?.flat_y);
    //#endregion Methods

    //#region Render time calcs
    //#endregion Render time calcs

    //#region Render
    return (
        <>
            {(isCurrentZoneItem?.length > 0 || isAllZoneItem?.length > 0) && (
                <Box className={classes.items}>
                    <Tooltip title={`${isDisplayAllZone ? 'Current Zone' : 'All Zones'}`}>
                        <IconButton
                            onClick={() => toggleZoneMedia(!isDisplayAllZone)}
                            size="large"
                            className={clsx(classes.allZoneMediaIcon, {
                                [classes.displayZoneMediaIcon]: isDisplayAllZone,
                            })}
                            sx={{ py: 0, px: '15px' }}
                        >
                            <IoMdInfinite className={`react-icon ${classes.zoneMediaIcon}`} />
                        </IconButton>
                    </Tooltip>
                    {allZoneItems && allZoneItems?.length > 0 && (
                        <Paper
                            component="ul"
                            ref={itemChipsRef}
                            className={clsx(classes.itemsWrapper, 'custom-scrollbar')}
                        >
                            {allZoneItems.map((item) => {
                                return (
                                    <li
                                        style={{
                                            marginRight: '10px',
                                        }}
                                        key={item?.item_id + '' + item?.zone_id}
                                    >
                                        <Tooltip title="Drag into scene to place item">
                                            <Chip
                                                label={item.display_name}
                                                size="small"
                                                className={classes.chip}
                                                onPointerDown={(event) => {
                                                    itemChipsRef.current.style.overflow = 'visible';
                                                    panoDraggable.handleMouseDown(
                                                        event,
                                                        event.currentTarget,
                                                        item,
                                                        onMovedHotspot,
                                                        false,
                                                        activeZone.orbit_frame === undefined ? true : false
                                                    );
                                                }}
                                            />
                                        </Tooltip>
                                    </li>
                                );
                            })}
                        </Paper>
                    )}
                </Box>
            )}
        </>
    );
    //#endregion Render
};

export { UnPositionedItemsBar };
